<template>
  <v-card flat>
    <v-card-title class="grey lighten-3">
      <v-row class="grey lighten-3">
        <v-icon class="draggable">mdi-drag</v-icon>
        <v-col class="pa-3">{{ supplier.name }} - Add Extras</v-col>
      </v-row>
      <v-container class="pa-0" fluid>
        <v-layout wrap pa-0>
          <v-flex sm4>
            <dates-range-picker
              custom-date-from-label="Valid From"
              custom-date-to-label="Valid To"
              :gtDateFromString="validFrom"
              :gtDateToString="validTo"
              @onRangeSelected="onDatesRangeSelected($event)"
              :outlined="outlinedInputs"
            ></dates-range-picker>
          </v-flex>
          <v-flex sm3 pl-3>
            <v-autocomplete
              :items="allAgentGroups"
              v-model="agentGroupId"
              label="Agent Group"
              item-text="name"
              item-value="id"
              :outlined="outlinedInputs"
            ></v-autocomplete>
          </v-flex>
          <v-flex sm3 pl-3>
            <gt-multi-select
              :items="
                supplier.services != null
                  ? supplier.services.edges.filter(filterNotInUse)
                  : []
              "
              v-model="selectedServicesIds"
              item-text="node.name"
              item-value="node.id"
              label="Services"
              :outlined="outlinedInputs"
            ></gt-multi-select>
          </v-flex>
          <v-flex sm2 pl-3>
            <v-text-field
              v-model="markup"
              label="Markup %"
              :outlined="outlinedInputs"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout wrap>
          <v-flex xs12>
            <v-layout wrap>
              <v-expansion-panels class="my-2">
                <v-expansion-panel>
                  <v-expansion-panel-header
                    >Visible Columns &amp; Filter</v-expansion-panel-header
                  >
                  <v-expansion-panel-content key="filters">
                    <v-layout row wrap align-baseline>
                      <v-flex xs12 sm8 class="pa-2">
                        <v-select
                          label="Visible Columns"
                          :items="extrasHeaders"
                          v-model="visibleExtrasColumns"
                          multiple
                          dense
                          hint="Show/Hide Columns"
                          persistent-hint
                          item-value="name"
                          chips
                          deletable-chips
                          :outlined="outlinedInputs"
                          @change="onVisibleColumnsChange"
                        ></v-select>
                      </v-flex>
                      <v-flex xs12 sm4 class="pa-2">
                        <v-text-field
                          append-icon="mdi-magnify"
                          label="Quick Filter..."
                          single-line
                          hide-details
                          v-model="search"
                          :outlined="outlinedInputs"
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-title>
    <v-card-text>
      <v-layout>
        <v-flex pt-3>
          <v-card outlined>
            <v-data-table
              v-model="selectedExtras"
              :loading="$apollo.loading"
              :headers="filteredExtrasHeaders"
              :items="extraEditableItems"
              :search="search"
              item-key="id"
              show-select
              v-bind="pagingOptions"
            >
              <template v-slot:body="{ items }">
                <tbody>
                  <tr
                    v-for="item in items"
                    :key="item.id"
                    :active="selectedExtras.indexOf(item) != -1"
                  >
                    <td>
                      <v-checkbox
                        :input-value="selectedExtras.indexOf(item) != -1"
                        primary
                        hide-details
                        class="ma-0"
                        @click.stop="onSelectExtra(item)"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('name')">
                      <v-text-field
                        v-model="item.name"
                        v-if="selectedExtras.indexOf(item) != -1"
                      />
                      <span v-else>{{ item.name }}</span>
                    </td>
                    <td v-if="showColumn('capacityChange')">
                      <v-checkbox
                        v-model="item.capacityChange"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('rateTypeCode')">
                      <rate-type-list-select
                        v-if="selectedExtras.indexOf(item) != -1"
                        :rate-type-code="item.rateTypeCode"
                        @onSelect="item.rateTypeCode = $event.code"
                        hideNotInUse
                      ></rate-type-list-select>
                      <span v-else>{{ item.rateTypeCode }}</span>
                    </td>
                    <td v-if="showColumn('taxCode')">
                      <taxes-list-select
                        v-if="selectedExtras.indexOf(item) != -1"
                        v-model="item.taxCode"
                      ></taxes-list-select>
                      <span v-else>{{ item.taxCode }}</span>
                    </td>
                    <td v-if="showColumn('childOnly')">
                      <v-checkbox
                        v-model="item.isChildOnly"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                        @change="onChildOnlyChange($event, item)"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('percentChild')">
                      <v-checkbox
                        v-model="item.percentChild"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('infantOnly')">
                      <v-checkbox
                        v-model="item.isInfantOnly"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                        @change="onInfantOnlyChange($event, item)"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('commission')">
                      <v-checkbox
                        v-model="item.commission"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('markup')">
                      <v-checkbox
                        v-model="item.markup"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('discount')">
                      <v-checkbox
                        v-model="item.discount"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('isMandatory')">
                      <v-checkbox
                        v-model="item.isMandatory"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('noReport')">
                      <v-checkbox
                        v-model="item.noReport"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('noVoucher')">
                      <v-checkbox
                        v-model="item.noVoucher"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('marketplace')">
                      <v-checkbox
                        v-model="item.marketplace"
                        primary
                        hide-details
                        :disabled="selectedExtras.indexOf(item) == -1"
                      ></v-checkbox>
                    </td>
                    <td v-if="showColumn('currencyCode')">
                      <currency-list-select
                        v-if="selectedExtras.indexOf(item) != -1"
                        :currency-code="item.currencyCode"
                        v-model="item.currencyCode"
                        @onSelect="item.currencyCode = $event"
                      ></currency-list-select>
                      <span v-else>{{ item.currencyCode }}</span>
                    </td>

                    <td v-if="showColumn('cost')">
                      <v-text-field
                        v-if="selectedExtras.indexOf(item) != -1"
                        label="Cost"
                        v-model="item.cost"
                        type="number"
                        min="0"
                        @change="onItemCostChange(item)"
                        :disabled="item.percentPrice.toString() != ''"
                      ></v-text-field>
                      <span v-else>{{ item.cost }}</span>
                    </td>
                    <td v-if="showColumn('sell')">
                      <v-text-field
                        v-if="selectedExtras.indexOf(item) != -1"
                        label="Sell"
                        v-model="item.sell"
                        type="number"
                        min="0"
                        :disabled="item.percentPrice.toString() != ''"
                      ></v-text-field>
                      <span v-else>{{ item.sell }}</span>
                    </td>
                    <td v-if="showColumn('percentPrice')">
                      <v-text-field
                        v-if="selectedExtras.indexOf(item) != -1"
                        label="% of price"
                        v-model="item.percentPrice"
                        type="number"
                        min="0"
                        :disabled="
                          (item.cost.toString() != '' &&
                            item.cost.toString() != '0') ||
                          (item.sell.toString() != '' &&
                            item.sell.toString() != '0')
                        "
                      ></v-text-field>
                      <span v-else>{{ item.percentPrice }}</span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>
    </v-card-text>
    <v-card-actions class="grey lighten-4">
      <v-layout>
        <v-flex class="text-right mr-4">
          <v-btn color="warning" text @click="onClose()">Cancel</v-btn>

          <v-btn
            color="secondary"
            text
            :disabled="selectedExtras.length == 0"
            @click="saveAndNewSeason"
            >Save &amp; New Season</v-btn
          >

          <v-btn
            color="secondary"
            text
            :disabled="selectedExtras.length == 0"
            @click="saveAndClear"
            >Save &amp; Clear</v-btn
          >
          <v-btn
            color="primary"
            @click="onSaveAndClose"
            :disabled="selectedExtras.length == 0"
            >Save</v-btn
          >
        </v-flex>
      </v-layout>
    </v-card-actions>
    <div v-if="debug">---{{ supplier.serviceType }}</div>
  </v-card>
</template>

<script>
import DatesRangePicker from "../../shared/DatesRangePicker";
import RateTypeListSelect from "../../shared/RateTypeListSelect";
import CurrencyListSelect from "../../shared/CurrencyListSelect";
import GtMultiSelect from "../../shared/GtMultiSelect";
import {
  ALL_EXTRA_SERVICE_TYPES_SEARCH_QUERY,
  AGENT_GROUPS_SEARCH_QUERY,
  SUPPLIER_ADD_EXTRAS_MUTATION,
} from "../../../../constants/graphql";
import { sortByDate, pagingOptions } from "../../../../helpers/index";
import TaxesListSelect from "../../shared/TaxesListSelect.vue";

const moment = require("moment");
const randomize = require("randomatic");

export default {
  name: "SupplierAddExtras",

  components: {
    DatesRangePicker,
    RateTypeListSelect,
    CurrencyListSelect,
    GtMultiSelect,
    TaxesListSelect,
  },

  props: {
    supplier: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      pagingOptions,
      debug: false,
      search: "",
      extraTypes: {},
      visibleExtrasColumns: this.$store.getters.localSettings(
        this.$options.name,
        [
          "name",
          "rateTypeCode",
          "childOnly",
          "infantOnly",
          "currencyCode",
          "percentPrice",
          "cost",
          "sell",
        ]
      ),
      extraEditableItems: [],
      selectedExtras: [],
      extrasHeaders: [
        {
          text: "Name",
          align: "left",
          value: "name",
          name: "name",
        },
        {
          text: "Capacity Change",
          align: "left",
          value: "capacityChange",
          name: "capacityChange",
        },
        {
          text: "Rate Type",
          align: "left",
          value: "rateTypeCode",
          name: "rateTypeCode",
        },
        {
          text: "Tax Code",
          align: "left",
          value: "taxCode",
          name: "taxCode",
        },
        {
          text: "Child Only",
          align: "left",
          value: "isChildOnly",
          name: "childOnly",
        },
        {
          text: "% Child",
          align: "left",
          value: "percentChild",
          name: "percentChild",
        },
        {
          text: "Infant Only",
          align: "left",
          value: "isInfantOnly",
          name: "infantOnly",
        },
        {
          text: "Commission",
          align: "left",
          value: "commission",
          name: "commission",
        },
        {
          text: "Markup",
          align: "left",
          value: "markup",
          name: "markup",
        },
        {
          text: "Discount",
          align: "left",
          value: "discount",
          name: "discount",
        },
        {
          text: "Mandatory",
          align: "left",
          value: "isMandatory",
          name: "isMandatory",
        },
        {
          text: "No Report",
          align: "left",
          value: "noReport",
          name: "noReport",
        },
        {
          text: "No Voucher",
          align: "left",
          value: "noVoucher",
          name: "noVoucher",
        },
        {
          text: "Marketplace",
          align: "left",
          value: "marketplace",
          name: "marketplace",
        },
        {
          text: "Currency",
          align: "left",
          value: "currencyCode",
          name: "currencyCode",
        },

        {
          text: "Cost",
          align: "left",
          value: "cost",
          name: "cost",
        },
        {
          text: "Sell",
          align: "left",
          value: "sell",
          name: "sell",
        },
        {
          text: "% of price",
          align: "left",
          value: "percentPrice",
          name: "percentPrice",
        },
      ],
      servicesHeaders: [
        {
          text: "Service",
          align: "left",
          value: "node.name",
          name: "name",
        },
      ],
      validFrom: null,
      validTo: null,
      /*
      https://tracker.globetrack.ie/issues/6087
      validFrom: moment()
        .utc()
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toISOString(true),
      validTo: moment()
        .utc()
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add(30, "days")
        .toISOString(true),
        */
      agentGroupId: "",
      selectedServicesIds: [],
      markup: 0,
    };
  },

  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    filteredExtrasHeaders() {
      return this.extrasHeaders.filter(
        (h) => this.visibleExtrasColumns.indexOf(h.name) > -1
      );
    },
    allAgentGroups() {
      if (this.agentGroups != null) {
        return [
          { name: "All Groups", id: "" },
          ...this.agentGroups.edges.map((a) => a.node),
        ];
      } else {
        return [];
      }
    },
  },

  methods: {
    saveAndNewSeason() {
      this.afterSaveAction = () => {
        //this.updateServiceRates();
        //this.selectedServiceRates = []; keep selected
        this.validFrom = moment(this.validTo).add(1, "day").toISOString(true);
        this.validTo = null;
      };
      this.onSave();
    },
    saveAndClear() {
      this.afterSaveAction = () => {
        this.selectedExtras = [];
        this.selectedServicesIds = [];
        this.validFrom = null;
        this.validTo = null;
        this.$emit("onSave", false);
      };
      this.onSave();
    },
    onSaveAndClose() {
      this.afterSaveAction = () => {
        this.$emit("onSave");
      };
      this.onSave();
    },
    onInfantOnlyChange(paylod, item) {
      if (paylod) {
        item.isChildOnly = false;
      }
    },
    onChildOnlyChange(paylod, item) {
      if (paylod) {
        item.isInfantOnly = false;
      }
    },
    onVisibleColumnsChange(event) {
      this.$store.dispatch("updateLocalSettings", {
        [this.$options.name]: event,
      });
    },
    filterNotInUse(e) {
      return !e.node.notInUse;
    },
    onDatesRangeSelected(data) {
      this.validFrom = data.dateFrom;
      this.validTo = data.dateTo;
    },
    showColumn(fieldName) {
      return this.visibleExtrasColumns.find((name) => name === fieldName);
    },
    selectAllServices(value) {
      this.selectedServicesIds = value
        ? this.supplier.services.edges.map((s) => s.node.id)
        : [];
    },
    onSelectExtra(item) {
      if (this.selectedExtras.indexOf(item) == -1) {
        this.selectedExtras.push(item);
      } else {
        this.selectedExtras.splice(this.selectedExtras.indexOf(item), 1);
      }
    },
    onSave() {
      if (this.areFieldsValidAlert()) {
        const extrasItems = this.createExtrasItems();

        this.$apollo
          .mutate({
            mutation: SUPPLIER_ADD_EXTRAS_MUTATION,
            variables: {
              supplierId: this.supplier.id,
              extras: extrasItems,
            },
          })
          .then((data) => {
            this.$store.dispatch("showAppSnackbar", {
              msg: "Extras added",
            });
            //this.$emit("onSave");
            this.afterSaveAction();
          })
          .catch((error) => alert(error));
      }
    },
    onItemCostChange(item) {
      const markup = Number(this.markup) / 100;
      item.sell = (Number(item.cost) * markup + Number(item.cost)).toFixed(2);
    },
    areFieldsValidAlert() {
      //https://tracker.globetrack.ie/issues/6087
      if (this.validFrom == null || this.validTo == null) {
        alert("Please select both valid from and valid to first");
        return false;
      }
      if (this.selectedServicesIds.length === 0) {
        alert("Please select services first");
        return false;
      }
      return true;
    },
    createExtrasItems() {
      const supplierExtrasItems = [];

      for (let serviceId of this.selectedServicesIds) {
        for (let extra of this.selectedExtras) {
          supplierExtrasItems.push({
            groupId: this.agentGroupId,
            typeId: extra.id,
            serviceId: serviceId,
            name: extra.name,
            cost: extra.cost === "" ? 0 : Number(extra.cost),
            sell: extra.sell === "" ? 0 : Number(extra.sell),
            percentPrice:
              extra.percentPrice === "" ? 0 : Number(extra.percentPrice),
            currencyCode: extra.currencyCode,
            rateTypeCode: extra.rateTypeCode,
            validFrom: this.validFrom,
            validTo: this.validTo,
            isChildOnly: Boolean(extra.isChildOnly),
            percentChild: Boolean(extra.percentChild),
            isInfantOnly: Boolean(extra.isInfantOnly),
            capacityChange: Boolean(extra.capacityChange),
            markup: Boolean(extra.markup),
            commission: Boolean(extra.commission),
            discount: Boolean(extra.discount),
            isMandatory: Boolean(extra.isMandatory),
            noReport: Boolean(extra.noReport),
            noVoucher: Boolean(extra.noVoucher),
            marketplace: Boolean(extra.marketplace),
            taxCode: extra.taxCode,
          });
        }
      }

      return supplierExtrasItems;
    },
    onClose() {
      this.$emit("onClose");
    },
  },

  apollo: {
    agentGroups: {
      query: AGENT_GROUPS_SEARCH_QUERY,
      variables() {
        return {
          filter: { isAgent: false },
        };
      },
      skip() {
        return this.supplier.id == null ? true : false;
      },

      fetchPolicy: "network-only",
      result(data) {},
    },
    extraTypes: {
      query: ALL_EXTRA_SERVICE_TYPES_SEARCH_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return {
          filter: {
            service_type: this.supplier.serviceType,
          },
        };
      },
      result({ data, loader, networkStatus }) {
        this.extraEditableItems = data.extraTypes.edges
          .map((item) => ({
            ...item.node,
            currencyCode:
              this.supplier.currencyCode != ""
                ? this.supplier.currencyCode
                : "EUR",
            percentPrice: "",
            cost: 0,
            sell: 0,
          }))
          .filter((item) => !item.notInUse);
      },
    },
  },
};
</script>

<style scoped>
.scrollable {
  overflow-y: scroll;
  max-height: 60vh;
  padding: 1px;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/suppliers/extras/SupplierAddExtras.vue